import { useState, useEffect } from 'react';
import { Modal, Table, Spin, Typography, Row, Col, Tag, Collapse } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import { toastError } from 'utils/constants/functions';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface MigoDetailModalProps {
  visible: boolean;
  onClose: () => void;
  purchaseOrderId: string;
}

interface ProcessData {
  id: string;
  purchaseOrderId: string;
  processType: string;
  processCode: string;
  url: string;
  status: boolean;
  errorMessage: string;
  uniqueCode: string;
  createId: string;
  createDate: string;
  modifyId: string;
  modifyDate: string;
  tcreateUser: {
    id: string;
    fullName: string;
  };
}

const MigoDetailModal = ({
  visible,
  onClose,
  purchaseOrderId,
}: MigoDetailModalProps) => {
  const [processData, setProcessData] = useState<ProcessData[]>([]);

  const { mutate: fetchProcessData, isPending: loading } = useMutation({
    mutationFn: () =>
      PurChaseInvoiceApi.getPurchaseOrderProcess({
        purchaseOrderId,
        status: false,
      }),
    onSuccess: (res: any) => {
      const data = res?.data?.getPurchaseOrderProcces?.data || [];
      setProcessData(data);
    },
    onError: (error) => {
      console.error('Fetch process data error:', error);
      toastError('Có lỗi xảy ra khi tải dữ liệu');
    },
  });

  useEffect(() => {
    if (visible && purchaseOrderId) {
      fetchProcessData();
    }
  }, [visible, purchaseOrderId, fetchProcessData]);

  // Columns cho bảng DMS
  const dmsColumns = [
    {
      title: 'Mã DMS',
      dataIndex: 'purchaseOrderId',
      width: 150,
      align: 'center' as const,
      render: (value: string) => value?.toString() ?? '-',
    },
    {
      title: 'Người tạo',
      dataIndex: 'tcreateUser',
      width: 120,
      align: 'center' as const,
      render: (user: any) => user?.fullName?.toString() ?? '-',
    },
    {
      title: 'Mô tả',
      dataIndex: 'processType',
      width: 100,
      align: 'center' as const,
      render: (value: string) => value?.toString() ?? '-',
    },
    {
      title: 'Thời gian',
      dataIndex: 'createDate',
      width: 150,
      align: 'center' as const,
      render: (value: string) =>
        value ? dayjs(value).format('DD/MM/YYYY H:mm') : '-',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: boolean) => (
        <Text
          style={{
            color: status ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold',
          }}
        >
          {status ? 'Thành công' : 'Thất bại'}
        </Text>
      ),
    },
  ];

  // Columns cho bảng SAP
  const sapColumns = [
    {
      title: 'Loại',
      dataIndex: 'processType',
      width: 80,
      align: 'center' as const,
      render: (value: string) => value?.toString() ?? '-',
    },
    {
      title: 'Mã',
      dataIndex: 'processCode',
      width: 120,
      align: 'center' as const,
      render: (value: string) => (
        <Text strong style={{ color: '#1890ff' }}>
          {value?.toString() ?? '-'}
        </Text>
      ),
    },
    {
      title: 'Chi tiết',
      dataIndex: 'url',
      width: 100,
      align: 'center' as const,
      render: (value: string) => (
        <Text style={{ color: '#1890ff', cursor: 'pointer' }}>Chi tiết</Text>
      ),
    },
    {
      title: 'Người tạo',
      dataIndex: 'tcreateUser',
      width: 120,
      align: 'center' as const,
      render: (user: any) => user?.fullName?.toString() ?? '-',
    },
    {
      title: 'Thời gian',
      dataIndex: 'createDate',
      width: 150,
      align: 'center' as const,
      render: (value: string) =>
        value ? dayjs(value).format('DD/MM/YYYY H:mm') : '-',
    },
    {
      title: 'Cập nhật',
      dataIndex: 'modifyDate',
      width: 150,
      align: 'center' as const,
      render: (value: string) =>
        value ? dayjs(value).format('DD/MM/YYYY H:mm') : '-',
    },
    {
      title: 'Mã lỗi',
      dataIndex: 'errorMessage',
      width: 200,
      align: 'center' as const,
      render: (value: string) => value?.toString() ?? '-',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: boolean) => (
        <Text
          style={{
            color: status ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold',
          }}
        >
          {status ? 'Thành công' : 'Thất bại'}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      title="Thông tin chứng từ"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Collapse
          defaultActiveKey={['1', '2']}
          ghost
          items={[
            {
              key: '1',
              label: 'Thông tin DMS',
              children: (
                <Table
                  columns={dmsColumns}
                  dataSource={processData}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  size="small"
                />
              ),
            },
            {
              key: '2',
              label: 'Thông tin SAP',
              children: (
                <Table
                  columns={sapColumns}
                  dataSource={processData}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  size="small"
                />
              ),
            },
          ]}
        />
      </Spin>
    </Modal>
  );
};

export default MigoDetailModal;
